import React, { useMemo } from 'react';
import type { CycleRing } from '@/lib/cycleTypes';
import { cn } from '@/lib/utils';
import { Eye, EyeOff } from 'lucide-react';

const ringColorFallbacks: Record<string, string> = {
  lunar: 'hsl(220, 60%, 70%)',
  moons: 'hsl(280, 60%, 60%)',
  days: 'hsl(45, 80%, 60%)',
  seals: 'hsl(0, 70%, 60%)',
  colors: 'hsl(160, 60%, 50%)',
  plasmas: 'hsl(320, 70%, 60%)',
};

const getStableRingFallback = (ringId: string) => {
  const mapped = ringColorFallbacks[ringId];
  if (mapped) return mapped;

  const seed = ringId.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
  const hue = (seed * 53) % 360;
  return `hsl(${hue},60%,60%)`;
};

const getRingColor = (ring: CycleRing, _index: number) => {
  const explicit = ring.colors?.[ring.currentStage ?? 0] ?? ring.colors?.[0];
  if (explicit) return `hsl(${explicit})`;
  return getStableRingFallback(ring.id);
};

interface ShortLegendWidgetProps {
  rings: CycleRing[];
  hiddenRings?: string[];
  onToggleRing?: (ringId: string) => void;
  className?: string;
}

export function ShortLegendWidget({
  rings,
  hiddenRings = [],
  onToggleRing,
  className,
}: ShortLegendWidgetProps) {
  const sortedRings = useMemo(() => {
    return [...rings].sort((a, b) => {
      const orderA = a.displayOrder ?? 999;
      const orderB = b.displayOrder ?? 999;
      return orderA - orderB;
    });
  }, [rings]);

  const visibleCount = rings.length - hiddenRings.length;

  return (
    <div className={cn("w-full h-full flex flex-col gap-2 px-4 py-3", className)}>
      {/* Header */}
      <div className="flex items-center justify-between shrink-0">
        <div className="flex items-center gap-2">
          <div className="w-1 h-4 rounded-full bg-gradient-to-b from-rose-400 to-rose-600" />
          <span className="text-xs font-semibold text-rose-100 tracking-wide">Cycle Legend</span>
        </div>
        <span className="text-[10px] text-rose-300/60">
          {visibleCount}/{rings.length}
        </span>
      </div>

      {/* Compact ring grid */}
      <div className="flex-1 min-h-0 overflow-hidden">
        <div className="flex flex-wrap gap-1 justify-start items-start content-start h-full overflow-y-auto">
          {sortedRings.map((ring, index) => {
            const isHidden = hiddenRings.includes(ring.id);
            const color = getRingColor(ring, index);
            const currentStage = (ring.currentStage ?? 0) + 1;
            const totalStages = ring.stages ?? 1;
            const currentLabel = ring.labels?.[ring.currentStage] ?? String(currentStage);
            
            return (
              <button
                key={ring.id}
                onClick={() => onToggleRing?.(ring.id)}
                className={cn(
                  "flex items-center gap-1.5 px-2 py-1 rounded-md transition-all text-[10px]",
                  isHidden
                    ? "bg-slate-900/40 opacity-50"
                    : "bg-teal-900/40 hover:bg-teal-800/50"
                )}
                style={{
                  borderLeft: `3px solid ${isHidden ? 'rgba(100,116,139,0.3)' : color}`,
                }}
                title={`${ring.name}: ${currentLabel} (${currentStage}/${totalStages})`}
              >
                {/* Ring info */}
                <div className="flex flex-col items-start leading-none">
                  <span className={cn(
                    "font-semibold truncate max-w-[60px]",
                    isHidden ? "text-slate-500" : "text-amber-100"
                  )}>
                    {ring.name ?? ring.id}
                  </span>
                  <span className={cn(
                    "text-[8px] tabular-nums",
                    isHidden ? "text-slate-600" : "text-amber-200/60"
                  )}>
                    {currentLabel} · {currentStage}/{totalStages}
                  </span>
                </div>
                
                {/* Visibility toggle icon */}
                {onToggleRing && (
                  <span className="ml-auto shrink-0">
                    {isHidden ? (
                      <EyeOff className="w-2.5 h-2.5 text-slate-500" />
                    ) : (
                      <Eye className="w-2.5 h-2.5 text-amber-300/70" />
                    )}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
